/* @img-migrated */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  MessageSquareQuote, Plus, Pencil, Trash2, Image as ImageIcon, Save, Loader2,
  X, Eye, EyeOff, Upload, GripVertical
} from 'lucide-react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = () => ({
  id: null,
  name: '',
  company: '',
  role: '',
  photo_url: '',
  quote: '',
  is_active: true,
  order: 0,
});

const AdminTestimonialsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Lock body scroll while editor modal is open
  useBodyScrollLock(!!editing);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/testimonials`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch {
      toast.error('Failed to load testimonials');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing(blank());
  const openEdit = (t) => setEditing({ ...blank(), ...t });
  const closeEditor = () => setEditing(null);

  const setField = (k) => (e) => {
    const v = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setEditing((prev) => ({ ...prev, [k]: v }));
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/testimonials/upload-photo`, fd, { withCredentials: true });
      setEditing((prev) => ({ ...prev, photo_url: r.data.url }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!editing.name?.trim() || !editing.quote?.trim()) {
      toast.error('Name and quote are required');
      return;
    }
    if (editing.quote.length > 280) {
      toast.error('Keep the quote under 280 characters');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        company: editing.company?.trim() || '',
        role: editing.role?.trim() || '',
        photo_url: editing.photo_url || null,
        quote: editing.quote.trim(),
        is_active: !!editing.is_active,
        order: parseInt(editing.order || 0, 10) || 0,
      };
      if (editing.id) {
        await axios.patch(`${API}/admin/testimonials/${editing.id}`, payload, { withCredentials: true });
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, { withCredentials: true });
        toast.success('Testimonial created');
      }
      closeEditor();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${t.id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const quickToggleActive = async (t) => {
    try {
      await axios.patch(`${API}/admin/testimonials/${t.id}`, { is_active: !t.is_active }, { withCredentials: true });
      load();
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="admin-page" data-testid="admin-testimonials">
      <header className="admin-page-header">
        <div className="admin-page-header__title flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
            <MessageSquareQuote className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="admin-eyebrow">Marketing</p>
            <h1 className="admin-h1 mt-0.5">Testimonials</h1>
            <p className="admin-subtitle mt-1.5">Member quotes shown in the autoplay carousel on the Membership page.</p>
          </div>
        </div>
        <div className="admin-page-header__actions">
          <button onClick={openNew} className="admin-btn admin-btn--primary" data-testid="new-testimonial-btn">
            <Plus className="w-4 h-4" /> New Testimonial
          </button>
        </div>
      </header>

      {/* List */}
      <div className="admin-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#002249] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="admin-empty m-5">
            <div className="admin-empty__icon"><MessageSquareQuote className="w-6 h-6" /></div>
            <p className="admin-empty__title">No testimonials yet</p>
            <p className="admin-empty__body">Add your first member quote to appear on the Membership page carousel.</p>
            <button onClick={openNew} className="admin-btn admin-btn--primary mt-4">
              <Plus className="w-4 h-4" /> Add Testimonial
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((t) => (
              <div key={t.id} className="p-4 sm:p-5 flex items-start gap-4 hover:bg-slate-50/60 transition-colors" data-testid={`testimonial-row-${t.id}`}>
                <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
                {t.photo_url ? (
                  <Img variant="generic" src={resolveImg(t.photo_url)} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2">
                    <p className="text-[14px] font-bold text-[#002249]">{t.name}</p>
                    {(t.role || t.company) && (
                      <p className="text-[12px] text-slate-500">· {t.role}{t.role && t.company ? ' · ' : ''}{t.company}</p>
                    )}
                    {!t.is_active && <span className="admin-pill admin-pill--neutral">Hidden</span>}
                    <span className="admin-pill admin-pill--navy">Order {t.order ?? 0}</span>
                  </div>
                  <p className="text-[13px] text-slate-700 mt-1 line-clamp-2 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => quickToggleActive(t)} className="admin-icon-btn" title={t.is_active ? 'Hide' : 'Show'} data-testid={`toggle-${t.id}`}>
                    {t.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(t)} className="admin-icon-btn" title="Edit" data-testid={`edit-${t.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(t)} className="admin-icon-btn admin-icon-btn--danger" title="Delete" data-testid={`delete-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      <ModalPortal>
        {editing && (
          <div className="fixed inset-0 z-[100] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-6" onClick={closeEditor}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="testimonial-editor"
            >
              <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="admin-eyebrow">{editing.id ? 'Edit' : 'Create'}</p>
                  <h2 className="admin-h2">{editing.id ? 'Edit Testimonial' : 'New Testimonial'}</h2>
                </div>
                <button onClick={closeEditor} className="admin-icon-btn"><X className="w-4 h-4" /></button>
              </div>

              <div className="p-6 space-y-4">
                {/* Photo */}
                <div>
                  <label className="admin-field-label">Photo</label>
                  <div className="flex items-center gap-4">
                    {editing.photo_url ? (
                      <Img variant="generic" src={resolveImg(editing.photo_url)} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                    <label className="admin-btn admin-btn--secondary cursor-pointer" data-testid="upload-photo-btn">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? 'Uploading…' : (editing.photo_url ? 'Change photo' : 'Upload photo')}
                      <input type="file" accept="image/*" className="hidden" onChange={onPhotoChange} />
                    </label>
                    {editing.photo_url && (
                      <button onClick={() => setEditing((p) => ({ ...p, photo_url: '' }))} className="text-[12px] text-rose-600 hover:underline">
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="admin-field-label">Name *</label>
                    <input type="text" value={editing.name} onChange={setField('name')} className="admin-input" placeholder="e.g. Carlos Reyes" data-testid="testimonial-name" />
                  </div>
                  <div>
                    <label className="admin-field-label">Order</label>
                    <input type="number" value={editing.order} onChange={setField('order')} className="admin-input" placeholder="0" data-testid="testimonial-order" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="admin-field-label">Role</label>
                    <input type="text" value={editing.role} onChange={setField('role')} className="admin-input" placeholder="CEO" data-testid="testimonial-role" />
                  </div>
                  <div>
                    <label className="admin-field-label">Company</label>
                    <input type="text" value={editing.company} onChange={setField('company')} className="admin-input" placeholder="Acme Inc." data-testid="testimonial-company" />
                  </div>
                </div>

                <div>
                  <label className="admin-field-label">Quote *</label>
                  <textarea
                    rows={4}
                    value={editing.quote}
                    onChange={setField('quote')}
                    maxLength={280}
                    className="admin-textarea"
                    placeholder="Being part of DCCF opened doors I didn’t even know existed."
                    data-testid="testimonial-quote"
                  />
                  <p className="text-[11px] text-slate-400 mt-1 text-right">{(editing.quote || '').length}/280</p>
                </div>

                <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={!!editing.is_active} onChange={setField('is_active')} className="rounded" data-testid="testimonial-active" />
                  Show on Membership page
                </label>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-2">
                <button onClick={closeEditor} className="admin-btn admin-btn--ghost" disabled={saving}>Cancel</button>
                <button onClick={save} className="admin-btn admin-btn--primary" disabled={saving} data-testid="save-testimonial">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </ModalPortal>
    </div>
  );
};

export default AdminTestimonialsPage;
